import { syntaxTree } from "@codemirror/language";
import { type Diagnostic, linter } from "@codemirror/lint";
import type { EditorView } from "@codemirror/view";
import {
	type AllModifiersType,
	type KeywordsType,
	LinkModifiers,
	controlCharsObject,
	keywordsObject,
	translateJadleToEnglish,
	text as sharedText,
} from "@quickpoint.me/shared";

const keywordsWithoutValue = ["pause:"];

/** Translate a locale token text (e.g. "titre:") to its English equivalent (e.g. "title:"). */
function toEnglish(text: string): string {
	return translateJadleToEnglish(text);
}

/** Exported for testing — the raw linter callback that produces diagnostics. */
export function lintJadle(view: EditorView): Diagnostic[] {
	const diagnostics: Diagnostic[] = [];
	let currentKeyword: KeywordsType | null = null;
	let keywordIndentLevel = -1;
	let currentKeywordStartLine = -1;

	let isGridOpen = false;
	let isCodeBlockOpen = false;
	let isStickyOpen = false;
	let isFooterStickyOpen = false;
	let gridStartLine = -1;
	let codeBlockStartLine = -1;
	let stickyStartLine = -1;
	let footerStickyStartLine = -1;

	for (let i = 1; i <= view.state.doc.lines; i++) {
		const line = view.state.doc.line(i);
		const text = line.text.trim();
		const indent = line.text.length - line.text.trimStart().length;

		// Indentation check: enforce exactly two spaces
		if (indent !== 0 && indent !== 2) {
			diagnostics.push({ from: line.from, to: line.from + indent, severity: "error", message: sharedText.lint.messages.incorrectIndentation });
		}

		// Page breaks reset everything
		if (text.startsWith(controlCharsObject["==="].controlChar)) {
			if (isGridOpen) diagnostics.push({ from: view.state.doc.line(gridStartLine).from, to: view.state.doc.line(gridStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedGridBeforePage });
			if (isCodeBlockOpen) diagnostics.push({ from: view.state.doc.line(codeBlockStartLine).from, to: view.state.doc.line(codeBlockStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedCodeBeforePage });
			if (isStickyOpen) diagnostics.push({ from: view.state.doc.line(stickyStartLine).from, to: view.state.doc.line(stickyStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedStickyHeaderBeforePage });
			if (isFooterStickyOpen) diagnostics.push({ from: view.state.doc.line(footerStickyStartLine).from, to: view.state.doc.line(footerStickyStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedStickyFooterBeforePage });

			isGridOpen = false; isCodeBlockOpen = false; isStickyOpen = false; isFooterStickyOpen = false; currentKeyword = null; keywordIndentLevel = -1;
			continue;
		}

		// Structural checks for grids and code blocks
		if (text.startsWith(controlCharsObject["---"].controlChar)) {
			// '---' starts a grid or a new row when a grid is already open.
			// Do not error when a grid is already open; multiple rows are allowed.
			if (!isGridOpen) { isGridOpen = true; gridStartLine = i; }
		} else if (text.startsWith(controlCharsObject["-->"].controlChar)) {
			if (!isGridOpen) diagnostics.push({ from: line.from, to: line.to, severity: "error", message: sharedText.lint.messages.closeGridWithoutOpen, actions: [{ name: sharedText.lint.actions.remove, apply: (view, from, to) => view.dispatch({ changes: { from, to, insert: "" } }) }] });
			isGridOpen = false;
		} else if (text.startsWith(controlCharsObject["^^^"].controlChar)) {
			isStickyOpen = !isStickyOpen;
			if (isStickyOpen) stickyStartLine = i;
		} else if (text.startsWith(controlCharsObject.vvv.controlChar)) {
			isFooterStickyOpen = !isFooterStickyOpen;
			if (isFooterStickyOpen) footerStickyStartLine = i;
		} else if (text.startsWith(controlCharsObject["```"].controlChar)) {
			isCodeBlockOpen = !isCodeBlockOpen;
			if (isCodeBlockOpen) codeBlockStartLine = i;
		}

		// Empty lines reset keyword context
		if (text === "") {
			currentKeyword = null;
			keywordIndentLevel = -1;
			continue;
		}

		const itemsOnLine: { name: string; from: number; to: number; text: string }[] = [];
		syntaxTree(view.state).iterate({
			from: line.from, to: line.to, enter: (node) => {
				if (node.name === "Keyword" || node.name === "Modifier" || node.name === "NotText" || node.name === "BackgroundKeyword" || node.name === "BackgroundModifier") {
					itemsOnLine.push({ name: node.name, from: node.from, to: node.to, text: view.state.sliceDoc(node.from, node.to) });
				}
			}
		});

		if (itemsOnLine.length > 1) {
			diagnostics.push({ from: itemsOnLine[0].from, to: itemsOnLine[itemsOnLine.length - 1].to, severity: "error", message: sharedText.lint.messages.multiInstructionOnLine });
			continue;
		}

		// First, handle lines that have a keyword or a modifier explicitly
		if (itemsOnLine.length === 1) {
			const item = itemsOnLine[0];
			const englishText = toEnglish(item.text);
			const remainingText = line.text.slice(item.to - line.from).trim();

			// Rule: Check for missing values (trim because external tokenizer tokens include leading whitespace)
			if (remainingText === "" && !keywordsWithoutValue.includes(englishText.trim())) {
				diagnostics.push({ from: item.from, to: item.to, severity: "error", message: sharedText.lint.messages.missingValue(item.text), actions: [{ name: sharedText.lint.actions.remove, apply: (view, from, to) => view.dispatch({ changes: { from, to, insert: "" } }) }] });
			}

			if (item.name === "Keyword" || item.name === "NotText" || item.name === "BackgroundKeyword") {
				currentKeyword = englishText as KeywordsType;
				keywordIndentLevel = indent;
				currentKeywordStartLine = i;
			} else if (item.name === "Modifier" || item.name === "BackgroundModifier") {
				// Coerce BackgroundModifier to a standard modifier for validation.
				// Trim because external tokenizer tokens include leading whitespace.
				const modifierText = item.name === "BackgroundModifier" ? "background:" : englishText.trim();

				if (currentKeyword && indent > keywordIndentLevel) {
					const isLinkModifier = (LinkModifiers as readonly string[]).includes(modifierText);
					if (isLinkModifier) {
						const blockText = view.state.doc.slice(view.state.doc.line(currentKeywordStartLine).from, line.from).toString();
						if (!blockText.includes("{{") || !blockText.includes("}}")) {
							diagnostics.push({
								from: item.from,
								to: item.to,
								severity: "error",
								message: `Don't forget to surround the text you want to become a link with {{...}} when using the "${modifierText}" modifier.`,
								actions: [{ name: sharedText.lint.actions.remove, apply: (view, from, to) => view.dispatch({ changes: { from, to, insert: "" } }) }]
							});
						}
					}

					const modifier = modifierText as AllModifiersType;
					const allowedModifiers = keywordsObject[currentKeyword]?.modifiers;
					if (allowedModifiers && !allowedModifiers.includes(modifier)) {
						diagnostics.push({ from: item.from, to: item.to, severity: "warning", message: sharedText.lint.messages.modifierNotAllowed(modifier, currentKeyword), actions: [{ name: sharedText.lint.actions.remove, apply: (view, from, to) => view.dispatch({ changes: { from, to, insert: "" } }) }] });
					}
				} else {
					diagnostics.push({ from: item.from, to: item.to, severity: "error", message: sharedText.lint.messages.modifierNeedsKeyword, actions: [{ name: sharedText.lint.actions.remove, apply: (view, from, to) => view.dispatch({ changes: { from, to, insert: "" } }) }] });
				}
			}
		} else {
			// This line has no keyword or modifier. Treat it as an implicit paragraph.
			// But first, if it's not indented appropriately, it breaks the previous context.
			if (currentKeyword !== null && indent <= keywordIndentLevel) {
				currentKeyword = null;
				keywordIndentLevel = -1;
			}

			// Now, establish the new context as a paragraph.
			currentKeyword = "paragraph:";
			keywordIndentLevel = indent;
		}
	}

	// Check for unclosed blocks at the end of the document
	if (isGridOpen) diagnostics.push({ from: view.state.doc.line(gridStartLine).from, to: view.state.doc.line(gridStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedGridAtDocEnd });
	if (isCodeBlockOpen) diagnostics.push({ from: view.state.doc.line(codeBlockStartLine).from, to: view.state.doc.line(codeBlockStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedCodeAtDocEnd });
	if (isStickyOpen) diagnostics.push({ from: view.state.doc.line(stickyStartLine).from, to: view.state.doc.line(stickyStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedStickyHeaderAtDocEnd });
	if (isFooterStickyOpen) diagnostics.push({ from: view.state.doc.line(footerStickyStartLine).from, to: view.state.doc.line(footerStickyStartLine).to, severity: "error", message: sharedText.lint.messages.unclosedStickyFooterAtDocEnd });

	return diagnostics;
}

export const simpleLinter = linter(lintJadle);
